import { useState, useEffect, useRef } from "react";
import { Loader2, CheckCircle2, Copy, Check, QrCode } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function SupplierPixDialog({ open, onOpenChange, pkg, needCpf, onDone }) {
  const [step, setStep] = useState("form");
  const [cpf, setCpf] = useState("");
  const [loading, setLoading] = useState(false);
  const [charge, setCharge] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (open) { setStep("form"); setCpf(""); setCharge(null); setLoading(false); setCopied(false); }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, pkg]);

  const startPolling = (orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const { data } = await api.get(`/pix/order/${orderId}`);
        if (["fulfilled", "activated"].includes(data.status)) {
          clearInterval(pollRef.current); setStep("done"); onDone?.();
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const create = async () => {
    if (needCpf && cpf.replace(/\D/g, "").length < 11) return toast.error("Informe um CPF válido.");
    setLoading(true);
    try {
      const { data } = await api.post("/reseller/buy-package", { package_id: pkg.id, method: "pix", cpf: cpf.trim() || null });
      setCharge(data); setStep("pay"); startPolling(data.order_id);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(charge?.brcode || ""); setCopied(true); toast.success("Código Pix copiado!"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Não foi possível copiar."); }
  };
  if (!pkg) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="supplier-pix-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Comprar {pkg.credits} créditos via Pix</DialogTitle>
          <DialogDescription>Pague no gateway do seu revendedor. Os créditos entram automaticamente.</DialogDescription>
        </DialogHeader>
        {step === "form" && (
          <div className="space-y-4">
            {needCpf && (
              <div className="space-y-2"><Label>CPF do pagador</Label><Input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="000.000.000-00" maxLength={14} data-testid="supplier-cpf-input" /></div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <span className="text-muted-foreground">Total</span>
              <span className="font-num text-lg font-bold text-primary">R$ {Number(pkg.price).toFixed(2)}</span>
            </div>
            <Button className="w-full gap-2 glow-red" onClick={create} disabled={loading} data-testid="supplier-generate-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />} Gerar Pix
            </Button>
          </div>
        )}
        {step === "pay" && charge && (
          <div className="flex flex-col items-center gap-4">
            {charge.brcode_base64?.startsWith("data:image")
              ? <img src={charge.brcode_base64} alt="QR Code Pix" className="h-52 w-52 rounded-lg border border-border bg-white p-2" data-testid="supplier-qr" />
              : <div className="flex h-52 w-52 items-center justify-center rounded-lg border border-border bg-muted"><QrCode className="h-16 w-16 text-muted-foreground" /></div>}
            <div className="text-center">
              <div className="font-num text-2xl font-bold text-primary">R$ {Number(charge.amount).toFixed(2)}</div>
              <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Aguardando pagamento...</p>
            </div>
            <div className="w-full space-y-2">
              <Label>Pix Copia e Cola</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={charge.brcode || ""} className="font-mono text-xs" data-testid="supplier-brcode" />
                <Button variant="outline" size="icon" onClick={copy} data-testid="supplier-copy-btn">{copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}</Button>
              </div>
            </div>
          </div>
        )}
        {step === "done" && (
          <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="supplier-done">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <div>
              <h3 className="font-heading text-lg font-semibold">Créditos adicionados!</h3>
              <p className="mt-1 text-sm text-muted-foreground">Seus {pkg.credits} créditos já estão disponíveis.</p>
            </div>
            <Button className="w-full" onClick={() => onOpenChange(false)} data-testid="supplier-close-btn">Fechar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
